/**
 * outreach_parse.ts · 09_APP · OS-006 · 2026-10-20 · C51, D-0086
 *
 * Her words: "could I use the microphone on the top and just say, hey, I just did outreach and served 15 or 18
 * women ... And then it will log it accordingly into the impact section".
 *
 * Pure function, no network: reads what she said or typed and returns the headcount when the sentence is about
 * women in a room. It never logs anything by itself. The Ask bar shows a confirm chip and only her tap writes.
 * "15 or 18 women" is a range she is unsure of, so it returns null rather than picking one for her.
 */

const ONES: Record<string, number> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
  nineteen: 19, a: 1, an: 1,
};
const TENS: Record<string, number> = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };

/** Spoken numbers up to 999, "twenty five", "twenty-five", "a hundred and twelve". */
export function wordsToNumber(s: string): number | null {
  const w = s.toLowerCase().replace(/-/g, " ").split(/\s+/).filter((x) => x && x !== "and");
  if (w.length === 0) return null;
  let total = 0, cur = 0, seen = false;
  for (const t of w) {
    const one = ONES[t], ten = TENS[t];
    if (one !== undefined) { cur += one; seen = true; }
    else if (ten !== undefined) { cur += ten; seen = true; }
    else if (t === "hundred") { cur = (cur || 1) * 100; seen = true; }
    else return null;
  }
  total += cur;
  return seen ? total : null;
}

const NUM_WORD = "(?:(?:a|an|zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|and)[\\s-]*)+";
const WHO = "(?:women|woman|ladies|girls|people|attendees)";

/**
 * The headcount in the sentence, or null.
 * Matches "18 women", "eighteen women", "twenty-five ladies", "there were 30 people in the room".
 * Returns null for a range ("15 or 18 women", "15 to 18 women") so the app asks for one number.
 */
export function parseHeadcount(said: string): number | null {
  const s = ` ${said.toLowerCase().replace(/[.,!?]/g, " ")} `;
  if (new RegExp(`\\b(\\d+|${NUM_WORD})\\s*(?:or|to)\\s*(\\d+|${NUM_WORD})\\s+${WHO}\\b`).test(s)) return null;
  const m = s.match(new RegExp(`(?:^|\\s)(\\d{1,4}|${NUM_WORD})\\s+${WHO}\\b`));
  if (!m || !m[1]) return null;
  const raw = m[1].trim();
  const n = /^\d+$/.test(raw) ? Number.parseInt(raw, 10) : wordsToNumber(raw);
  return n !== null && n > 0 && n < 5000 ? n : null;
}

/** True when the sentence is about a talk or outreach, so "18 women applied" is not logged as a room. */
export function soundsLikeOutreach(said: string): boolean {
  return /\b(outreach|talk|talked|spoke|speak|spoken|presentation|presented|workshop|session|class|room|served|there were|was there|showed up|attended|in attendance)\b/i.test(said);
}
